import { APP_CONSTANTS } from '@/config/constants';
import { databaseService } from '@/services/databaseService';
import {
  accountedArchiveIdFor,
  parsePendingTerminalResolution,
  type PendingOperationSnapshotInput,
} from '@/services/sync/PendingOperationTerminalization';
import { runControlledSupersededTerminalization } from '@/services/sync/PendingOperationTerminalizationRuntime';
import type {
  PendingRecoveryItem,
  RecoveryIdentity,
  RecoveryState,
  RemoteRecoveryReceipt,
} from '@/types/recovery';
import { RecoveryEscrowService } from './RecoveryEscrowService';
import { RecoveryGateService } from './RecoveryGateService';
import { SafeResetCoordinator } from './SafeResetCoordinator';

interface RecoveryOrchestratorDependencies {
  gate: RecoveryGateService;
  escrow: Pick<RecoveryEscrowService, 'prepareAndUpload' | 'refreshRemoteReceipt'>;
  reset: Pick<SafeResetCoordinator, 'execute'>;
  terminalizeSuperseded?: (input: {
    identity: RecoveryIdentity;
    recoveryId: string;
    receipt: RemoteRecoveryReceipt;
    operation: PendingOperationSnapshotInput;
    expectedPayloadSha512: string;
    successorEntityId: string;
    writeSetCount: number;
    writeSetDigest: string;
  }) => Promise<{ archiveId: string }>;
  readPendingOperation?: (id: string) => Promise<PendingOperationSnapshotInput | null>;
}

export type RecoveryContinueResult =
  | { status: 'awaiting_validation'; state: RecoveryState }
  | { status: 'completed'; state: RecoveryState };

const pendingSourceId = (pendingId: string): string => {
  const prefixed = pendingId.match(/^(pending_operations|pending_deletions):(.+)$/);
  return prefixed?.[2] ?? pendingId;
};

const assertReceiptAccountsForPending = (state: RecoveryState): void => {
  if (!state.validatedReceipt) throw new Error('Receipt validado ausente.');
  const signedItems = state.validatedReceipt.receipt.attestationPayload.pendingItems;
  const signedById = new Map(signedItems.map(item => [item.localOperationId, item.payloadSha512]));
  const localItems = Object.values(state.pendingItems);
  if (signedById.size !== signedItems.length) {
    throw new Error('Receipt com localOperationId duplicado.');
  }
  if (signedById.size !== localItems.length) {
    throw new Error('Receipt nao contabiliza exatamente as pendencias capturadas.');
  }
  for (const item of localItems) {
    if (signedById.get(item.pendingId) !== item.sourceDigest) {
      throw new Error(`Receipt diverge da pendencia capturada: ${item.pendingId}.`);
    }
  }
};

const defaultReadPending = async (id: string) => (
  databaseService.readItem<PendingOperationSnapshotInput>(
    APP_CONSTANTS.DATABASE.TABLES.PENDING_OPERATIONS.NAME,
    id
  )
);

const defaultTerminalize = async (input: {
  identity: RecoveryIdentity;
  recoveryId: string;
  receipt: RemoteRecoveryReceipt;
  operation: PendingOperationSnapshotInput;
  expectedPayloadSha512: string;
  successorEntityId: string;
  writeSetCount: number;
  writeSetDigest: string;
}) => runControlledSupersededTerminalization({
  companyId: input.identity.companyId,
  userId: input.identity.userId,
  recoveryId: input.recoveryId,
  receiptId: input.receipt.receiptId,
  operation: input.operation,
  expectedPayloadSha512: input.expectedPayloadSha512,
  successorEntityId: input.successorEntityId,
  writeSetCount: input.writeSetCount,
  writeSetDigest: input.writeSetDigest,
});

export class RecoveryOrchestrator {
  constructor(private readonly dependencies: RecoveryOrchestratorDependencies) {}

  start(input: {
    identity: RecoveryIdentity;
    recoveryId: string;
    consentedAt: string;
    pendingItems: Array<Pick<PendingRecoveryItem, 'pendingId' | 'sourceDigest'>>;
  }): Promise<RecoveryState> {
    return this.dependencies.escrow.prepareAndUpload(input);
  }

  async continueAfterTrustedValidation(
    identity: RecoveryIdentity,
    recoveryId: string
  ): Promise<RecoveryContinueResult> {
    let state = await this.dependencies.gate.getState(identity);
    if (!state || state.recoveryId !== recoveryId) {
      throw new Error('Recuperacao local nao encontrada.');
    }
    if (state.phase === 'completed') return { status: 'completed', state };

    if (state.phase === 'awaiting_validation') {
      state = await this.dependencies.escrow.refreshRemoteReceipt(identity, recoveryId);
      if (state.phase === 'awaiting_validation') {
        return { status: 'awaiting_validation', state };
      }
    }

    if (state.phase === 'validated') {
      assertReceiptAccountsForPending(state);
      state = await this.accountSignedTerminalResolutions(identity, recoveryId, state);
      const unaccounted = Object.values(state.pendingItems)
        .filter(item => item.classification === 'unaccounted');
      if (unaccounted.length > 0) {
        state = await this.dependencies.gate.classifyPendingItems(
          identity,
          recoveryId,
          unaccounted.map(item => ({
            pendingId: item.pendingId,
            classification: 'escrowed_verified',
            evidenceRef: state!.validatedReceipt!.receipt.receiptId,
            reason: 'preserved_in_validated_recovery_escrow',
          }))
        );
      }
    }

    const completed = await this.dependencies.reset.execute(identity);
    return { status: 'completed', state: completed };
  }

  private async accountSignedTerminalResolutions(
    identity: RecoveryIdentity,
    recoveryId: string,
    state: RecoveryState
  ): Promise<RecoveryState> {
    const receipt = state.validatedReceipt?.receipt;
    if (!receipt) throw new Error('Receipt validado ausente.');
    const terminalize = this.dependencies.terminalizeSuperseded ?? defaultTerminalize;
    const readPending = this.dependencies.readPendingOperation ?? defaultReadPending;
    let next = state;
    for (const signed of receipt.attestationPayload.pendingItems) {
      if (!signed.terminalResolution) continue;
      const resolution = parsePendingTerminalResolution(signed.terminalResolution);
      const local = next.pendingItems[signed.localOperationId];
      if (!local) {
        throw new Error(`Pendencia local ausente do receipt: ${signed.localOperationId}.`);
      }
      if (!signed.entityId) {
        throw new Error(`entityId ausente na resolucao superseded: ${signed.localOperationId}.`);
      }
      if (local.sourceDigest !== signed.payloadSha512) {
        throw new Error(`Digest local diverge da resolucao assinada: ${signed.localOperationId}.`);
      }
      const sourceId = pendingSourceId(local.pendingId);
      const operation = await readPending(sourceId);
      if (!operation) {
        throw new Error(`Pendencia ativa ausente para terminalizar: ${local.pendingId}.`);
      }
      const originId = String(operation.itemId ?? operation.item_id ?? '').toLowerCase();
      if (originId !== signed.entityId.toLowerCase()) {
        throw new Error(`Origem local diverge da attestation: ${local.pendingId}.`);
      }
      const result = await terminalize({
        identity,
        recoveryId,
        receipt,
        operation: { ...operation, id: sourceId },
        expectedPayloadSha512: signed.payloadSha512,
        successorEntityId: resolution.successorEntityId,
        writeSetCount: resolution.writeSetCount,
        writeSetDigest: resolution.writeSetSha512,
      });
      const evidenceRef = accountedArchiveIdFor(sourceId, signed.payloadSha512);
      if (result.archiveId !== evidenceRef) {
        throw new Error(`Arquivo canonico diverge da terminalizacao: ${local.pendingId}.`);
      }
      next = await this.dependencies.gate.classifyPendingItems(identity, recoveryId, [{
        pendingId: local.pendingId,
        classification: 'superseded_by_remote_entity',
        evidenceRef,
        reason: 'superseded_by_signed_terminal_resolution',
      }]);
    }
    return next;
  }
}
